import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import copyToClipboard from 'copy-to-clipboard';

import { USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';
import { SCREEN_ID_APARTMENT_LOCATOR_ADD_LEAD } from 'client/constants/screenIds';
import { SR_API_ACTION_APARTMENT_LOCATOR_ADD_LEAD } from 'api/actionTypes';
import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants';

import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import ButtonDeprecated, { BUTTON_THEME_YELLOW } from 'client/components/ButtonDeprecated';
import H1 from 'client/components/H1';
import SrFormikField from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import bedroomOptionItems from 'client/helpers/bedroomOptionItems';
import { leaseSituationShortOptionItems } from 'common/leaseSituationOptionItems';
import { actionDataTableLoadItems } from 'client/helpers/dataTables';

const formFields = [
  {
    id: 'name',
    title: 'full name',
    type: 'short_text',
    properties: {
      placeholder: 'John Smith',
    },
    validations: ['required'],
  },
  {
    id: 'phone',
    title: 'phone',
    type: 'phone',
    properties: {
      placeholder: '5121234567',
    },
    validations: ['required', 'phone'],
  },
  {
    id: 'email',
    title: 'email',
    type: 'email',
    properties: {
      placeholder: 'jsmith@gmail.com',
    },
    validations: ['required', 'email'],
  },
  {
    id: 'password',
    title: 'password',
    type: 'short_text',
    validations: ['required'],
  },
  {
    id: 'leaseSituation',
    title: 'current lease situation',
    type: 'multiple_choice',
    properties: {
      choices: leaseSituationShortOptionItems,
      multipleSelection: false,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
  {
    id: 'moveInDate',
    title: 'move-in date',
    type: 'date_picker',
    properties: {
      placeholder: 'Choose date...',
    },
    validations: ['required'],
  },
  {
    id: 'bedrooms',
    title: 'bedrooms',
    type: 'multiple_choice',
    properties: {
      choices: bedroomOptionItems,
      multipleSelection: true,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
  {
    id: 'minRentPrice',
    title: 'minimum rent price',
    type: 'currency',
    validations: ['required'],
  },
  {
    id: 'maxRentPrice',
    title: 'maximum rent price',
    type: 'currency',
    validations: ['required'],
  },
  {
    id: 'pets',
    title: 'pets',
    type: 'multiple_choice',
    properties: {
      choices: [
        {
          label: 'Dog',
          value: 'dog',
        },
        {
          label: 'Cat',
          value: 'cat',
        },
      ],
      multipleSelection: true,
      allowEmptyArray: true,
    },
  },
  {
    id: 'source',
    title: 'sunroom or personal client?',
    type: 'multiple_choice',
    properties: {
      choices: [
        {
          label: 'Sunroom',
          value: 'sunroom',
        },
        {
          label: 'Craigslist',
          value: 'personal',
        },
        {
          label: 'Facebook',
          value: 'facebook',
        },
        {
          label: 'Referral',
          value: 'referral',
        },
        {
          label: 'Repeat Client',
          value: 'repeatClient',
        },
        {
          label: 'Other',
          value: 'other',
        },
      ],
      multipleSelection: false,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
];

const formInitialState = createInitialValues({ formFields, defaultValues: {} });

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  addNewLeadApiState: {
    action: SR_API_ACTION_APARTMENT_LOCATOR_ADD_LEAD,
  },
  loadDataTableItemsApiState: {
    action: DATA_TABLE_SOURCE_AGENT_LEADS,
  },
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_ADD_LEAD)
@branch({
  currentUser: ['currentUser'],
  appUrl: ['env', 'APP_URL'],
})
class AgentAddLeadPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Create Lead | Sunroom Rentals',
    section: 'Create Lead',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    addNewLeadApiState: PropTypes.object.isRequired,
    loadDataTableItemsApiState: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props);

    const { appUrl, currentUser } = props;

    this.state = {
      inviteLink:  `${appUrl}/invite?invitedBy=${currentUser.id}&marketId=${currentUser.staffMarketId}`,
      copiedLink: false,
    };

    this._unmounting = false;
    this._onAddLead = this._onAddLead.bind(this);
  }

  componentWillUnmount() {
    this._unmounting = true;
  }

  async _onAddLead(values) {
    const { dispatch, history, currentUser } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_LOCATOR_ADD_LEAD,
      payload: {
        userValues: values,
      },
      onSuccess: (renterUserId) => {
        dispatch(actionDataTableLoadItems, {
          dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
          viewId: `leads-id-${renterUserId}`,
          filter: {
            apartmentLocatorUserId: currentUser.id,
            renterUserId: renterUserId,
          },
          count: 1,
        }).then(() => {
          history.replace('/agent/leads');
        });
      },
    });
  }

  _onCopyInviteLink = () => {
    const { inviteLink } = this.state;
    copyToClipboard(inviteLink);

    this.setState({
      copiedLink: true,
    });
    setTimeout(() => {
      if (!this._unmounting) {
        this.setState({
          copiedLink: false,
        });
      }
    }, 1200);
  }

  render() {
    const { currentUser, addNewLeadApiState, loadDataTableItemsApiState } = this.props;
    const { copiedLink } = this.state;
    const requestInProgress = addNewLeadApiState.inProgress || loadDataTableItemsApiState.inProgress;
    const inviteLink = this.state.inviteLink.replace(/https?:\/\//, '');

    return (
      <div
        className='AgentAddLeadPage page-agentDashboard lead add '
      >
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={(
            <SrLink
              scrollToLastPosition
              redirectToLastLocation
              className='back link'
              to='/agent/leads'
            />
          )}
        />

        <div className='contentContainer'>
          <div className='inviteLinkContainer'>
            <H1>Invite link</H1>
            <span className='description'>
              Any renter who signs up with your invite link will be assigned to you. Send it around!
            </span>

            <div className='inviteLinkBox' onClick={() => this._onCopyInviteLink(inviteLink)}>
              <span className='link'>
                {inviteLink}
              </span>
              <span className='text'>{copiedLink ? 'Copied Link!' : 'Copy Link'}</span>
            </div>
          </div>

          <Formik
            validateOnChange
            initialValues={formInitialState}
            validate={createFormValidationFn(formFields)}
            onSubmit={this._onAddLead}
            render={(formikBag) => {
              return (
                <div>
                  <div className='header'>
                    <H1>Create a lead</H1>
                  </div>
                  <Form className='leadForm'>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                    ))}
                    <div className='bottomActions'>
                      {addNewLeadApiState.error && (
                        <div className='formMessage error'>
                          <span>{`${addNewLeadApiState.error.message} Please contact Sunroom Support.`}</span>
                        </div>
                      )}
                      <ButtonDeprecated
                        theme={BUTTON_THEME_YELLOW}
                        onClick={formikBag.handleSubmit}
                        disabled={!formikBag.isValid || requestInProgress}
                      >
                        {requestInProgress ? 'loading...' : 'create lead'}
                      </ButtonDeprecated>
                    </div>
                  </Form>
                </div>
              );
            }}
          />
        </div>
      </div>
    );
  }
}

AgentAddLeadPage.displayName = 'AgentAddLeadPage';

export default AgentAddLeadPage;
